/**
 * Retorno do login à autorização de um agente do MCP (change platform-mcp-server, S-2.16a).
 *
 * O Hermes abre `/mcp/autorizar?<query do /authorize>` no navegador do Admin. Sem sessão, a
 * query precisa sobreviver ao login (senha, TOTP, às vezes o cadastro do MFA) para a página de
 * consentimento recomeçar dela. Ela fica só no `sessionStorage`, que já é por aba e some com a
 * aba, sob uma chave fixa, junto do instante da chegada do pedido, e é apagada depois do uso, quando vence
 * ({@link PENDING_AUTHORIZATION_TTL_MS}) e no "Sair" do Shell. Nunca no `localStorage`: a query carrega o
 * `state` e o `code_challenge` do pedido, e não tem por que durar além da aba que o abriu.
 *
 * O valor guardado é dado para a API, nunca destino de navegação: depois do login, a SPA volta
 * sempre ao caminho fixo {@link MCP_AUTHORIZE_PATH}, qualquer que seja o conteúdo da chave. Code
 * e token nunca passam por aqui.
 */

/** O único destino de retorno do login quando há autorização pendente. */
export const MCP_AUTHORIZE_PATH = '/mcp/autorizar';

/** Chave fixa da query pendente no `sessionStorage`. */
export const PENDING_AUTHORIZATION_KEY = 'platform-admin-mcp-authorize-query';

/** Destino padrão depois do login, sem autorização pendente. */
export const DEFAULT_POST_LOGIN_PATH = '/tenants';

function sessionStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    // Navegador com storage bloqueado: o retorno ao consentimento só não acontece.
    return null;
  }
}

/**
 * Validade da query pendente, contada da chegada do pedido do Hermes: a mesma do pedido na API
 * (`PlatformMcp:OAuth:RequestMinutes`, padrão 10). Passado esse prazo, o Hermes já não espera
 * a volta, e um login posterior na mesma aba não pode reabrir um pedido velho em nome do Admin.
 * Regravar a query (recusa de sessão, "Entrar de novo") preserva o instante da chegada: o prazo
 * nunca recomeça.
 */
export const PENDING_AUTHORIZATION_TTL_MS = 10 * 60 * 1000;

/** A query pendente e o instante da chegada do pedido que a trouxe. */
export interface PendingAuthorization {
  query: string;
  savedAt: number;
}

function isFresh(savedAt: number): boolean {
  const age = Date.now() - savedAt;
  return age >= 0 && age <= PENDING_AUTHORIZATION_TTL_MS;
}

function parsePending(raw: string): PendingAuthorization | null {
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return null;
    const { query, savedAt } = value as Record<string, unknown>;
    if (typeof query !== 'string' || !query) return null;
    if (typeof savedAt !== 'number' || !Number.isFinite(savedAt)) return null;
    return { query, savedAt };
  } catch {
    return null;
  }
}

/**
 * Guarda a query crua do pedido (com ou sem `?`). Sem `savedAt`, é a chegada de um pedido e vale
 * o instante atual, salvo se a mesma query já está guardada e válida (aí vale o instante dela);
 * numa regravação, passe o `savedAt` lido de {@link readPendingAuthorization},
 * para o prazo seguir contado da chegada. Query vazia não é guardada, e uma regravação já vencida
 * (ou "no futuro") apaga a chave em vez de gravar.
 */
export function savePendingAuthorizationQuery(query: string, savedAt?: number): void {
  const value = query.startsWith('?') ? query.slice(1) : query;
  if (!value) return;
  // A mesma query ainda válida no storage é o mesmo pedido (o link reaberto, a guarda que roda de
  // novo): o instante da chegada é o já gravado, e o prazo não recomeça.
  const existing = readPendingAuthorization();
  const original = existing?.query === value ? existing.savedAt : undefined;
  const instant = savedAt ?? original ?? Date.now();
  if (!Number.isFinite(instant) || !isFresh(instant)) {
    clearPendingAuthorizationQuery();
    return;
  }
  const pending: PendingAuthorization = { query: value, savedAt: instant };
  try {
    sessionStore()?.setItem(PENDING_AUTHORIZATION_KEY, JSON.stringify(pending));
  } catch {
    // Cota estourada ou storage bloqueado: segue sem o retorno automático.
  }
}

/**
 * A query pendente com o instante da chegada, ou `null`. A query é dado para a API: nunca vira
 * URL de navegação nem texto na tela. Valor fora do formato, vencido ou gravado "no futuro"
 * (relógio que voltou) é apagado e lido como ausente.
 */
export function readPendingAuthorization(): PendingAuthorization | null {
  let raw: string | null;
  try {
    raw = sessionStore()?.getItem(PENDING_AUTHORIZATION_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  const pending = parsePending(raw);
  if (!pending || !isFresh(pending.savedAt)) {
    clearPendingAuthorizationQuery();
    return null;
  }
  return pending;
}

/** Só a query de {@link readPendingAuthorization}, ou `null`. */
export function readPendingAuthorizationQuery(): string | null {
  return readPendingAuthorization()?.query ?? null;
}

export function clearPendingAuthorizationQuery(): void {
  try {
    sessionStore()?.removeItem(PENDING_AUTHORIZATION_KEY);
  } catch {
    // Nada a apagar num storage bloqueado.
  }
}

/**
 * Sessão perdida (refresh recusado, rota protegida sem token) fora da página de consentimento:
 * quem cai no login a partir dali não está voltando a um pedido do agente, e a chave pendente sai,
 * para o próximo login na aba não reabrir o pedido. Na própria {@link MCP_AUTHORIZE_PATH} a chave
 * fica: a página a regrava com o `savedAt` original e o login volta a ela.
 */
export function clearPendingAuthorizationOutsideConsent(pathname: string): void {
  if (pathname === MCP_AUTHORIZE_PATH || pathname.startsWith(`${MCP_AUTHORIZE_PATH}/`)) return;
  clearPendingAuthorizationQuery();
}

export function hasPendingAuthorization(): boolean {
  return readPendingAuthorizationQuery() !== null;
}

/**
 * Para onde o login leva: o caminho fixo do consentimento quando há query pendente, a lista de
 * tenants quando não há. O conteúdo da chave nunca decide o destino.
 */
export function postLoginPath(): string {
  return hasPendingAuthorization() ? MCP_AUTHORIZE_PATH : DEFAULT_POST_LOGIN_PATH;
}
